<template>
  <div class="auto-screen-root" ref="rootEl">
    <DismissalBigScreen
      v-if="mode === 'dismissal'"
      :external-data="dismissalData"
      :skip-fetch="!isMockMode"
    />

    <div v-else class="content-screen">
      <div class="content-header">
        <div class="title">{{ screenTitle }}</div>
        <div class="clock">{{ currentTime }}</div>
      </div>

      <div class="content-body" v-if="pageItems.length">
        <div class="content-grid" :class="'mode-' + layoutMode">
          <div
            v-for="(item, idx) in pageItems"
            :key="(item && item.Id) || ('empty-' + idx)"
            class="content-cell"
            :class="item ? ('kind-' + contentKind(item)) : 'is-empty-slot'"
          >
            <template v-if="item">
              <span class="content-badge">{{ contentBadge(item) }}</span>
              <div
                v-if="contentTypeOf(item) === 1"
                class="media-block"
                :class="{ 'media-block--with-caption': hasImageCaption(item) }"
              >
                <div class="media-image-wrap">
                  <img
                    class="media-image"
                    :src="displayMediaUrl(item)"
                    alt="轮播图片"
                  />
                </div>
                <div v-if="hasImageCaption(item)" class="media-caption">
                  {{ trimText(item.TextContent || item.textContent) }}
                </div>
              </div>
              <div v-else-if="contentTypeOf(item) === 2" class="text-content">
                <div class="text-card">{{ item.TextContent || item.textContent || "暂无文字内容" }}</div>
              </div>
              <div v-else class="empty-box">当前内容类型暂不支持视频</div>
            </template>
            <div v-else class="empty-box empty-box--slot"> </div>
          </div>
        </div>
      </div>

      <div v-else class="empty-box">暂无已发布内容</div>
    </div>
  </div>
</template>

<script>
import { BaseApiUrl } from "@/api/base";
import { getDismissalBigScreenAutoData } from "@/api/dismissal";
import DismissalBigScreen from "@/views/DismissalBigScreen.vue";
import {
  batchResolveOssMediaUrls,
  isAliyunOssHttpsUrl,
  stripOssUrlQuery
} from "@/utils/mediaUrl";

export default {
  name: "DismissalBigScreenAuto",
  components: { DismissalBigScreen },
  data() {
    return {
      mode: "dismissal",
      dismissalData: null,
      screenTitle: "放学大屏",
      contentList: [],
      /** 学校 SchId，用于 OSS 预签名 */
      ossSchoolId: "",
      /** 稳定 OSS 地址 -> 预签名展示地址 */
      mediaUrlMap: {},
      currentIndex: 0,
      violationLayoutMode: 1,
      pollTimer: null,
      switchTimer: null,
      clockTimer: null,
      currentTime: "",
      // 用于避免轮询过快导致“内容不变但被重复 apply 重置轮播”
      lastContentSignature: "",
      // 全屏触发：需要用户手势；用一次性监听避免频繁 requestFullscreen 报错
      fullscreenTriggerHandler: null
    };
  },
  computed: {
    /** URL 加 ?mock=1 时走子组件本地模拟数据，不请求接口 */
    isMockMode() {
      const mockQuery = this.$route.query.mock;
      return mockQuery === "1" || mockQuery === "true" || mockQuery === "yes";
    },
    currentItem() {
      if (!this.contentList.length) return null;
      return this.contentList[this.currentIndex] || null;
    },
    layoutMode() {
      return this.violationLayoutMode === 2 || this.violationLayoutMode === 4 ? this.violationLayoutMode : 1;
    },
    pageItems() {
      if (!this.contentList.length) return [];
      if (this.layoutMode === 1) {
        return this.currentItem ? [this.currentItem] : [];
      }
      const items = [];
      for (let i = 0; i < this.layoutMode; i++) {
        items.push(this.contentList[this.currentIndex + i] || null);
      }
      return items;
    }
  },
  mounted() {
    this.updateClock();
    this.clockTimer = setInterval(this.updateClock, 1000);
    if (!this.isMockMode) {
      this.fetchAutoData();
      this.pollTimer = setInterval(this.fetchAutoData, 10000);
    }

    // Chrome/Edge 需要用户手势才能 requestFullscreen：这里挂一个一次性监听
    // 用户点击页面后会自动进入全屏；如果完全不触发手势，则浏览器不会允许全屏
    this.fullscreenTriggerHandler = () => {
      this.handleFirstUserGesture();
    };
    document.addEventListener("click", this.fullscreenTriggerHandler, { once: true });
    document.addEventListener("touchstart", this.fullscreenTriggerHandler, { once: true });
  },
  beforeDestroy() {
    this.clearSwitchTimer();
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.clockTimer) clearInterval(this.clockTimer);
    if (this.fullscreenTriggerHandler) {
      document.removeEventListener("click", this.fullscreenTriggerHandler);
      document.removeEventListener("touchstart", this.fullscreenTriggerHandler);
    }
  },
  methods: {
    handleFirstUserGesture() {
      // 1) 尝试全屏
      this.tryRequestFullscreen();
    },
    tryRequestFullscreen() {
      this.$nextTick(() => {
        const el = this.$refs.rootEl;
        if (!el) return;
        try {
          if (document.fullscreenElement) return;
          const request =
            el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
          if (typeof request === "function") request.call(el);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("[DismissalBigScreenAuto] requestFullscreen failed:", e);
        }
      });
    },
    updateClock() {
      this.currentTime = new Date().toLocaleString("zh-CN", { hour12: false });
    },
    /** 格子类型：violation / text / image / video。创建于 2026-08-12 */
    contentKind(item) {
      if (!item) return "empty";
      const cat = item.ContentCategory != null ? item.ContentCategory : item.contentCategory;
      if (Number(cat) === 1) return "violation";
      const type = this.contentTypeOf(item);
      if (type === 2) return "text";
      if (type === 3) return "video";
      return "image";
    },
    /** 兼容 PascalCase / camelCase 的内容类型。创建于 2026-08-12 */
    contentTypeOf(item) {
      if (!item) return 0;
      const type = item.ContentType != null ? item.ContentType : item.contentType;
      return Number(type) || 0;
    },
    /** 左上角类型角标文案。创建于 2026-08-12 */
    contentBadge(item) {
      const kind = this.contentKind(item);
      if (kind === "violation") return "违停";
      if (kind === "text") return "文字";
      if (kind === "video") return "视频";
      return "图片";
    },
    clearSwitchTimer() {
      if (this.switchTimer) {
        clearTimeout(this.switchTimer);
        this.switchTimer = null;
      }
    },
    calcContentSignature(list) {
      if (!Array.isArray(list) || list.length === 0) return "";
      // 顺序无关签名：避免后端每次返回顺序变化导致反复重置轮播定时器
      const parts = list
        .map(x => {
          const idPart = x && x.Id != null ? String(x.Id) : "";
          const typePart = x && x.ContentType != null ? String(x.ContentType) : "";
          // 兜底：如果 Id 不稳定，使用媒体/文本信息参与签名
          const urlPart = x && x.MediaUrl ? stripOssUrlQuery(String(x.MediaUrl)) : "";
          const textPart = x && x.TextContent ? String(x.TextContent) : "";
          return `${idPart}_${typePart}_${urlPart}_${textPart}_${this.layoutMode}`;
        })
        .sort();
      return parts.join("#");
    },
    toAbsoluteUrl(url) {
      if (!url) return "";
      if (/^https?:\/\//i.test(url)) return url;
      return `${BaseApiUrl}${url}`;
    },
    /** 轮播图展示地址：OSS 用预签名，其它拼本机前缀 */
    displayMediaUrl(item) {
      const raw = item && item.MediaUrl ? String(item.MediaUrl).trim() : "";
      if (!raw) return "";
      if (raw.startsWith("blob:") || raw.startsWith("data:")) return raw;
      if (!/^https?:\/\//i.test(raw)) return this.toAbsoluteUrl(raw);
      const stable = stripOssUrlQuery(raw);
      if (isAliyunOssHttpsUrl(stable) && this.mediaUrlMap[stable]) {
        return this.mediaUrlMap[stable];
      }
      return raw;
    },
    /** 批量预签本页内容里的 OSS 图片。创建于 2026-08-12 */
    async resolveContentMediaUrls(list) {
      const schoolId = this.ossSchoolId;
      const urls = (list || [])
        .filter(x => x && x.ContentType === 1 && x.MediaUrl)
        .map(x => String(x.MediaUrl).trim())
        .filter(Boolean);
      if (!schoolId || !urls.length) {
        this.mediaUrlMap = {};
        return;
      }
      try {
        const map = await batchResolveOssMediaUrls(urls, schoolId, { inline: true });
        this.mediaUrlMap = map || {};
      } catch (e) {
        console.warn("[DismissalBigScreenAuto] OSS 预签名失败", e);
      }
    },
    trimText(text) {
      return text != null ? String(text).trim() : "";
    },
    /** 图片类型且附带说明文字（与后台「附加说明」一致） */
    hasImageCaption(item) {
      if (!item || this.contentTypeOf(item) !== 1) return false;
      return this.trimText(item.TextContent || item.textContent).length > 0;
    },
    scheduleNextForNonVideo() {
      this.clearSwitchTimer();
      if (!this.contentList.length) return;
      const pageCount = Math.ceil(this.contentList.length / this.layoutMode);
      if (pageCount <= 1) return;
      this.switchTimer = setTimeout(() => {
        this.nextItem();
      }, 8000);
    },
    nextItem() {
      if (!this.contentList.length) return;
      const step = this.layoutMode;
      const pageCount = Math.max(1, Math.ceil(this.contentList.length / step));
      const page = Math.floor(this.currentIndex / step);
      const nextPage = (page + 1) % pageCount;
      this.currentIndex = nextPage * step;
      this.$nextTick(() => {
        this.scheduleNextForNonVideo();
      });
    },
    applyContentList(list) {
      const oldId = this.currentItem ? this.currentItem.Id : null;
      this.contentList = Array.isArray(list) ? list : [];
      if (!this.contentList.length) {
        this.currentIndex = 0;
        this.clearSwitchTimer();
        this.mediaUrlMap = {};
        return;
      }
      const idx = oldId ? this.contentList.findIndex(x => x.Id === oldId) : -1;
      const step = this.layoutMode;
      const raw = idx >= 0 ? idx : 0;
      this.currentIndex = Math.floor(raw / step) * step;
      this.resolveContentMediaUrls(this.contentList).then(() => {
        this.$nextTick(() => {
          this.scheduleNextForNonVideo();
        });
      });
    },
    fetchAutoData() {
      const code = this.$route.query.code || "";
      if (!code) return;
      getDismissalBigScreenAutoData({ code })
        .then(res => {
          if (!res || !res.data || !res.data.success || !res.data.response) return;
          const payload = res.data.response;
          const schId =
            payload.SchId ||
            payload.schId ||
            (payload.DismissalData && (payload.DismissalData.SchId || payload.DismissalData.schId)) ||
            "";
          if (schId) this.ossSchoolId = String(schId);
          const layout = payload.ViolationLayoutMode != null ? payload.ViolationLayoutMode : payload.violationLayoutMode;
          this.violationLayoutMode = layout === 2 || layout === 4 ? Number(layout) : 1;
          this.screenTitle = payload.SchoolName && payload.ScreenName
            ? `${payload.SchoolName} - ${payload.ScreenName}`
            : (payload.ScreenName || "放学大屏");

          if (payload.DismissalData) {
            this.dismissalData = payload.DismissalData;
          }

          const nextMode = payload.Mode === "content" ? "content" : "dismissal";
          // 模式切换：dismissal<->content 需要重置轮播
          if (nextMode !== this.mode) {
            this.mode = nextMode;
            if (this.mode === "content") {
              const nextList = payload.ContentList || [];
              this.lastContentSignature = this.calcContentSignature(nextList);
              this.applyContentList(nextList);
            } else {
              this.clearSwitchTimer();
            }
            return;
          }

          // 同模式下：content 模式只在“内容真的变化”时刷新列表
          if (this.mode === "content") {
            const nextList = payload.ContentList || [];
            const nextSignature = this.calcContentSignature(nextList);
            if (nextSignature !== this.lastContentSignature) {
              this.lastContentSignature = nextSignature;
              this.applyContentList(nextList);
            } else {
              // 内容未变也刷新预签名，避免长时播放后 OSS 签名过期
              this.resolveContentMediaUrls(this.contentList);
            }
          }
        })
        .catch(() => {});
    }
  }
};
</script>

<style scoped>
.auto-screen-root {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: #0f172a;
}

.content-screen {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  color: #fff;
}

.content-header {
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 28px;
  background: rgba(30, 41, 59, 0.9);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.title {
  font-size: 28px;
  font-weight: 700;
}

.clock {
  font-size: 22px;
  font-weight: 600;
}

.content-body {
  flex: 1;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  min-height: 0;
}
.content-grid {
  width: 100%;
  height: 100%;
  display: grid;
  gap: 12px;
  padding: 12px;
  box-sizing: border-box;
}
.content-grid.mode-1 {
  grid-template-columns: 1fr;
}
.content-grid.mode-2 {
  grid-template-columns: 1fr 1fr;
}
.content-grid.mode-4 {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
}
.content-cell {
  position: relative;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: #000;
  border-radius: 8px;
}
.content-cell.kind-text {
  background:
    radial-gradient(80% 70% at 50% 40%, rgba(30, 58, 95, 0.55), transparent 70%),
    linear-gradient(180deg, #0b1220 0%, #020617 100%);
}
.content-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 5;
  font-size: clamp(12px, 1.5vmin, 18px);
  line-height: 1;
  padding: 6px 10px;
  border-radius: 4px;
  background: rgba(15, 23, 42, 0.82);
  color: #e2e8f0;
  font-weight: 600;
  pointer-events: none;
}
.kind-violation .content-badge {
  color: #fbbf24;
}
.kind-text .content-badge {
  color: #93c5fd;
}
.content-grid.mode-2 .text-card,
.content-grid.mode-4 .text-card {
  font-size: clamp(24px, 3.4vmin, 42px);
}
.content-grid.mode-2 .media-caption,
.content-grid.mode-4 .media-caption {
  font-size: clamp(16px, 2.2vmin, 28px);
  padding: 10px 16px 14px;
}

/* 图片铺满格子；说明叠在底部，格子高度不受文案长短影响 */
.media-block {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: #000;
}

.media-image-wrap,
.media-image,
.media-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.media-image,
.media-video {
  object-fit: cover;
  background: #000;
}

.media-caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  max-height: 4.8em;
  padding: 14px 24px 16px;
  box-sizing: border-box;
  font-size: clamp(16px, 2.2vmin, 28px);
  line-height: 1.45;
  font-weight: 600;
  color: #f8fafc;
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0) 0%, rgba(15, 23, 42, 0.72) 28%, rgba(15, 23, 42, 0.92) 100%);
  overflow: hidden;
  word-break: break-word;
  white-space: pre-wrap;
}

/* 文字格：满格居中排版，控制行宽但不过度留白 */
.text-content {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(36px, 5vmin, 56px) clamp(18px, 3vmin, 36px);
  box-sizing: border-box;
  background: transparent;
}

.text-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-size: clamp(28px, 4.2vmin, 56px);
  line-height: 1.55;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-align: center;
  color: #f8fafc;
  text-shadow: 0 2px 18px rgba(2, 6, 23, 0.45);
  background: none;
  border: none;
  border-radius: 0;
  box-shadow: none;
  word-break: break-word;
  white-space: pre-wrap;
}

.empty-box {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 34px;
  color: #94a3b8;
}

.empty-box--slot {
  width: 100%;
  height: 100%;
  background:
    linear-gradient(145deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95));
  border: 1px dashed rgba(100, 116, 139, 0.45);
  border-radius: 8px;
  color: rgba(148, 163, 184, 0.55);
  font-size: clamp(14px, 1.8vmin, 22px);
}

</style>
